import { ImagePlus, UploadCloud } from "lucide-react";
import { useRef, useState } from "react";

import { cn } from "../../lib/cn";
import { Button } from "../ui/Button";

interface UploadDropzoneProps {
  disabled?: boolean;
  onSelectFile: (file: File) => Promise<void> | void;
}

export function UploadDropzone({ disabled = false, onSelectFile }: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = async (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) {
      return;
    }
    await onSelectFile(file);
  };

  return (
    <div
      className={cn(
        "rounded-[30px] border border-dashed px-6 py-10 text-center transition",
        isDragging ? "border-accent bg-accent/10 shadow-glow" : "border-white/15 bg-white/[0.03]",
        disabled && "pointer-events-none opacity-60"
      )}
      onDragEnter={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        setIsDragging(false);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragging(false);
        void handleFile(event.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        accept="image/*"
        className="hidden"
        type="file"
        onChange={(event) => {
          void handleFile(event.target.files);
          event.target.value = "";
        }}
      />
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-accent/10 text-accent-strong">
        {isDragging ? <UploadCloud className="h-8 w-8" /> : <ImagePlus className="h-8 w-8" />}
      </div>
      <div className="space-y-2">
        <p className="font-display text-xl font-semibold text-white">Upload an image to begin</p>
        <p className="mx-auto max-w-lg text-sm leading-6 text-ink-soft">
          Drop an image into the workspace or choose a file. The image stays in memory for this session only and
          disappears on refresh.
        </p>
      </div>
      <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Button onClick={() => inputRef.current?.click()}>Choose Image</Button>
        <span className="text-xs uppercase tracking-[0.18em] text-ink-muted">PNG, JPG, WEBP up to 10 MB</span>
      </div>
    </div>
  );
}
